import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { DarkSectionBackdrop } from "@/components/DarkSectionBackdrop";
import { useLinkSet, getIcon, isExternalHref } from "@/lib/site-content";

const linkHover =
  "inline-block border-b border-background/30 pb-0.5 transition-colors hover:border-primary hover:text-primary";

export function SiteFooter() {
  const navItems = useLinkSet("footer_nav");
  const connectItems = useLinkSet("footer_connect");
  const legalItems = useLinkSet("footer_legal");
  return (
    <footer className="relative isolate overflow-hidden bg-foreground text-background">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 opacity-50 blur-2xl"
      >
        <DarkSectionBackdrop intensity="soft" sparkles={false} arcs={false} />
      </div>
      <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-background/80">
              Currently open to roles
            </p>
            <h2 className="mt-4 inline-block font-display text-3xl leading-[1.05] md:text-4xl lg:text-5xl text-balance">
              Let's build something{" "}
              <span className="whitespace-nowrap">
                <span className="italic text-gradient">worth</span> following.
              </span>
            </h2>
            <Link
              to="/contact"
              className={
                "mt-8 inline-flex items-center gap-3 border-b border-background/30 pb-1 font-mono text-xs uppercase tracking-[0.2em] text-background hover:border-white " +
                linkHover
              }
            >
              Start a conversation <ArrowUpRight className="h-4 w-4" />
            </Link>
            <div className="mt-8 max-w-sm">
              <p className="sheen inline-block font-mono text-[14px] uppercase tracking-[0.25em]">
                <span className="text-gradient">The Digital Debrief Newsletter</span>
              </p>
              <p className="mt-2 text-sm text-background/75">
                Receive monthly field notes on AI, Web3, privacy, <br className="hidden sm:block" />
                and the internet's shifting systems.
              </p>
              <Link
                to="/newsletter"
                className="lift-glow group mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-white shadow-[0_8px_28px_-6px_oklch(0.6_0.27_305/0.55)] transition-transform hover:-translate-y-0.5"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, oklch(0.66 0.26 28), oklch(0.6 0.28 305), oklch(0.6 0.26 265))",
                }}
              >
                Subscribe <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-background/80">
              Navigation
            </p>
            <ul className="mt-4 space-y-2 font-display text-lg">
              {navItems.map((item) => (
                <li key={item.item_key}>
                  {isExternalHref(item.href) ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkHover}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href as any} className={linkHover}>
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-background/80">
              Connect With Me
            </p>
            <ul className="mt-4 space-y-2 font-display text-lg">
              {connectItems.map((item) => {
                const Icon = getIcon(item.icon);
                const external = isExternalHref(item.href);
                const inner = (
                  <>
                    {Icon ? <Icon className="h-4 w-4" /> : null} {item.label}
                  </>
                );
                return (
                  <li key={item.item_key}>
                    {external ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={"inline-flex items-center gap-2 " + linkHover}
                      >
                        {inner}
                      </a>
                    ) : (
                      <a href={item.href} className={"inline-flex items-center gap-2 " + linkHover}>
                        {inner}
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        <div className="mt-16 flex flex-col justify-between gap-4 border-t border-background/15 pt-6 text-xs text-background/75 md:flex-row">
          <p>© {new Date().getFullYear()} Scott Cunningham. All rights reserved.</p>
          <p className="flex gap-4 font-mono uppercase tracking-[0.2em]">
            {legalItems.map((item) => (
              <Link key={item.item_key} to={item.href as any} className={linkHover}>
                {item.label}
              </Link>
            ))}
          </p>
        </div>
      </div>
    </footer>
  );
}
